
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Bell, PlayCircle, Search, ChevronDown, Menu, Grid3x3, Settings, 
  FileText, CreditCard, Archive, ArrowRightLeft, Check,
  User, LogOut, Sun, Moon, Users, Globe
} from './Icons';
import { useTranslation } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';

interface TopbarProps {
  toggleSidebar: () => void;
  collapsed: boolean;
}

const Topbar: React.FC<TopbarProps> = ({ toggleSidebar, collapsed }) => {
  const [isAppSwitcherOpen, setIsAppSwitcherOpen] = useState(false);
  const [isTenantDropdownOpen, setIsTenantDropdownOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [currentTenant, setCurrentTenant] = useState('VMI Services AG');
  const [isDarkMode, setIsDarkMode] = useState(true); // Default to Dark Mode
  const [mounted, setMounted] = useState(false); // Track mount status

  useEffect(() => {
    // Set mounted to true after component mounts
    setMounted(true);
  }, []);

  useEffect(() => {
    // Only check and update the theme after the component has mounted
    if (mounted) {
      const savedTheme = localStorage.getItem('theme');
      if (savedTheme) {
        setIsDarkMode(savedTheme === 'dark');
      }
    }
  }, [mounted]); // Runs only when mounted is true

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);
  
  const { language, setLanguage, t } = useTranslation();
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const switcherRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);

  // useEffect(() => {
  //   if (isDarkMode) {
  //     document.documentElement.classList.add('dark');
  //     localStorage.setItem('theme', 'dark');
  //   } else {
  //     document.documentElement.classList.remove('dark');
  //     localStorage.setItem('theme', 'light');
  //   }
  // }, [isDarkMode]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (switcherRef.current && !switcherRef.current.contains(event.target as Node)) {
        setIsAppSwitcherOpen(false);
        setIsTenantDropdownOpen(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
      if (langRef.current && !langRef.current.contains(event.target as Node)) {
        setIsLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const apps = [
    { name: 'Stock Workflow', icon: FileText, color: 'bg-brand-medium', iconColor: 'text-white' },
    { name: 'Consignment', icon: CreditCard, color: 'bg-teal-600', iconColor: 'text-white' },
    { name: 'Archive', icon: Archive, color: 'bg-amber-700', iconColor: 'text-white' },
    { name: 'Compare', icon: ArrowRightLeft, color: 'bg-indigo-600', iconColor: 'text-white' },
  ];

  const handleSignOut = () => {
    logout();
    navigate('/login');
  };

  return (
    <header className="h-12 bg-white dark:bg-[#0A0A0A] border-b border-gray-200 dark:border-[#262626] flex items-center justify-between px-0 sticky top-0 z-[60] text-[#161616] dark:text-white transition-colors duration-300">
      <div className="flex items-center flex-1 h-full">
        <button onClick={toggleSidebar} className="h-full w-12 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-[#1A1A1A] text-gray-600 dark:text-white border-r border-gray-200 dark:border-[#262626] transition-colors">
           <span className="sr-only">Toggle Sidebar</span>
           <Menu size={20} />
        </button>

        <div className="relative w-full max-w-lg h-full hidden md:block border-r border-gray-200 dark:border-[#262626]">
           <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
             <Search size={16} />
           </div>
           <input 
            type="text" 
            placeholder={t('search_resources')} 
            className="w-full h-full pl-12 pr-4 bg-white dark:bg-[#0A0A0A] text-sm text-[#161616] dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:bg-gray-50 dark:focus:bg-[#111111] transition-colors"
           />
        </div>
      </div>

      <div className="flex items-center h-full">
        <div className="hidden lg:flex items-center h-full border-l border-gray-200 dark:border-[#262626]">
           <button className="h-full px-6 flex items-center gap-2 text-xs font-bold uppercase tracking-wider hover:bg-gray-100 dark:hover:bg-[#1A1A1A] transition-colors text-gray-600 dark:text-white">
            <PlayCircle size={14} className="text-brand-bright" />
            {t('product_tour')}
          </button>
        </div>

        <button 
          onClick={() => setIsDarkMode(!isDarkMode)}
          className="h-full w-12 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-[#1A1A1A] border-l border-gray-200 dark:border-[#262626] transition-colors"
        >
          {isDarkMode ? <Sun size={18} className="text-white" /> : <Moon size={18} className="text-gray-600" />}
        </button>

        {/* Language Picker */}
        <div className="relative h-full" ref={langRef}>
          <div 
            onClick={() => setIsLangOpen(!isLangOpen)}
            className="h-full px-4 flex items-center gap-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-[#1A1A1A] border-l border-gray-200 dark:border-[#262626] transition-colors text-gray-600 dark:text-white"
          >
            <span className="text-xs font-bold uppercase">{language}</span>
            <ChevronDown size={12} />
          </div>

          {isLangOpen && (
            <div className="absolute top-full right-0 bg-white dark:bg-[#111111] border border-gray-200 dark:border-[#262626] shadow-lg z-[100] min-w-[150px]">
              <button 
                onClick={() => { setLanguage('en'); setIsLangOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 text-xs font-bold uppercase hover:bg-gray-100 dark:hover:bg-[#1A1A1A] text-[#161616] dark:text-white transition-colors"
              >
                English
              </button>
              <button 
                onClick={() => { setLanguage('de'); setIsLangOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 text-xs font-bold uppercase hover:bg-gray-100 dark:hover:bg-[#1A1A1A] text-[#161616] dark:text-white transition-colors"
              >
                Deutsch
              </button>
            </div>
          )}
        </div>

        <button 
           onClick={() => navigate('/settings/general')}
           className="h-full w-12 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-[#1A1A1A] border-l border-gray-200 dark:border-[#262626] transition-colors"
         >
           <Settings size={18} className="text-gray-600 dark:text-white" />
         </button>

        <div className="relative h-full" ref={switcherRef}>
          <button 
             onClick={() => setIsAppSwitcherOpen(!isAppSwitcherOpen)}
             className={`h-full w-12 flex items-center justify-center border-l border-gray-200 dark:border-[#262626] hover:bg-gray-100 dark:hover:bg-[#1A1A1A] transition-colors ${isAppSwitcherOpen ? 'bg-gray-100 dark:bg-[#1A1A1A]' : ''}`} 
           >
             <Grid3x3 size={18} className="text-gray-600 dark:text-white" />
           </button>

           {isAppSwitcherOpen && (
             <div className="absolute top-full right-0 w-80 bg-white dark:bg-[#111111] border border-gray-200 dark:border-[#262626] shadow-xl z-[100]">
                <div onClick={() => setIsTenantDropdownOpen(!isTenantDropdownOpen)} className="px-4 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-[#1A1A1A] cursor-pointer border-b border-gray-200 dark:border-[#262626] transition-colors">
                  <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-brand-medium flex items-center justify-center text-xs font-bold text-white">VI</div>
                      <div className="flex flex-col">
                        <span className="text-sm font-bold text-[#161616] dark:text-white">{currentTenant}</span>
                        <span className="text-[10px] uppercase tracking-wider text-gray-400">Enterprise</span>
                      </div>
                  </div>
                  <ChevronDown size={16} className="text-gray-500 dark:text-white" />
                </div>

                <div className="p-0">
                   {apps.map((app) => (
                     <button key={app.name} className="w-full flex items-center gap-4 px-4 py-3 hover:bg-gray-50 dark:hover:bg-[#1A1A1A] border-b border-gray-200 dark:border-[#262626] last:border-0 group transition-colors">
                       <div className={`w-8 h-8 flex items-center justify-center ${app.color} text-white`}>
                          <app.icon size={16} />
                       </div>
                       <span className="text-sm font-medium text-gray-700 dark:text-gray-200 group-hover:text-brand-dark dark:group-hover:text-white">{app.name}</span>
                     </button>
                   ))}
                </div>
             </div>
           )}
        </div>

        <div className="relative h-full" ref={profileRef}>
          <div 
            onClick={() => setIsProfileOpen(!isProfileOpen)}
            className={`h-full flex items-center gap-3 px-4 cursor-pointer border-l border-gray-200 dark:border-[#262626] hover:bg-gray-100 dark:hover:bg-[#1A1A1A] transition-colors ${isProfileOpen ? 'bg-gray-100 dark:bg-[#1A1A1A]' : ''}`} 
          >
            <div className="w-6 h-6 bg-brand-bright flex items-center justify-center text-black font-bold text-[10px] rounded-full">
              {user?.name.split(' ').map(n => n[0]).join('') || 'DK'}
            </div>
            {!collapsed && <ChevronDown size={12} className="text-gray-600 dark:text-white" />}
          </div>

          {isProfileOpen && (
            <div className="absolute top-full right-0 w-64 bg-white dark:bg-[#111111] border border-gray-200 dark:border-[#262626] shadow-xl z-[100]">
              <div className="px-4 py-4 border-b border-gray-200 dark:border-[#262626]">
                <p className="text-sm font-bold text-[#161616] dark:text-white">{user?.name || 'Daniel Kohler'}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{user?.email || 'd.kohler@io-market.com'}</p>
              </div>
              <div>
                <button 
                  onClick={() => {
                    navigate('/settings/general');
                    setIsProfileOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-[#1A1A1A] text-sm text-gray-700 dark:text-gray-200 transition-colors"
                >
                  <User size={16} />
                  {t('profile_settings')}
                </button>
                <button 
                  onClick={handleSignOut}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-[#1A1A1A] text-sm text-red-600 dark:text-red-400 transition-colors"
                >
                  <LogOut size={16} />
                  {t('sign_out')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Topbar;
